<template>
    <ion-page>
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="end">
                    <ion-button @click="close()">
                        <ion-icon slot="icon-only" :icon="closeOutline"></ion-icon>
                    </ion-button>
                </ion-buttons>
                <ion-title>{{ shipment.id?'Update':'Create' }} List</ion-title>
            </ion-toolbar>
        </ion-header>
        <ion-content :fullscreen="true">
            <ion-list>
                <ion-item lines="none" >
                    <ion-label position="stacked">Reference</ion-label>
                    <ion-input v-model="shipment.reference" placeholder="List Reference" />
                </ion-item>
                <ion-item lines="none" >
                    <ion-label position="stacked">Reference</ion-label>
                    <ion-select v-model="shipment.method" placeholder="Method">
                        <ion-select-option value="HMT">Pallets</ion-select-option>
                        <ion-select-option value="UPS">Boxes</ion-select-option>
                    </ion-select>
                </ion-item>
                <ion-item lines="none" >
                    <ion-label position="stacked">Priority</ion-label>
                    <ion-select v-model.number="shipment.priority.id" placeholder="Priority">
                        <ion-select-option :value="0">Not Set</ion-select-option>
                        <ion-select-option :value="1">Low</ion-select-option>
                        <ion-select-option :value="2">Medium</ion-select-option>
                        <ion-select-option :value="3">High</ion-select-option>
                        <ion-select-option :value="4">Urgent</ion-select-option>
                    </ion-select>
                </ion-item>
                <ion-item lines="none" >
                    <ion-label position="stacked">Due Date</ion-label>
                    <ion-datetime v-model="shipment.must_ship_by" displayFormat="DD MMM YYYY" placeholder="Due date"/>
                </ion-item>
                <ion-item lines="none" >
                    <ion-label position="stacked">Owner</ion-label>
                    <user-select permission="shipments.store" v-model="shipment.owner" placeholder="select owner" />
                </ion-item>
                <ion-item v-if="shipment.lines && shipment.lines.length > 0" lines="none">
                    <ion-label>Send list to picking</ion-label>
                    <ion-checkbox v-model="pick">Send to Picking</ion-checkbox>
                </ion-item>
            </ion-list>
        </ion-content>
        <ion-footer>
            <ion-row>
                <ion-col v-if="shipment.id" >
                    <ion-button
                        expand="block"
                        size="large"
                        @click="manageItems"
                    >
                        Edit Lines
                    </ion-button>
                </ion-col>
                <ion-col>
                    <ion-button expand="block" size="large" @click="submit()" :disabled="disabled">
                        {{ isEdit?'Update':'Create' }}
                    </ion-button>
                </ion-col>
            </ion-row>
        </ion-footer>
    </ion-page>
</template>

<script>
import {modalController} from '@ionic/vue';
import {
    IonContent, IonPage, IonLabel, IonItem, IonHeader, IonToolbar,
    IonTitle, IonButtons, IonIcon, IonList, IonFooter, IonButton,
    IonInput, IonSelect, IonSelectOption, IonDatetime,
    IonRow, IonCol, IonCheckbox
} from '@ionic/vue';

import {closeOutline} from 'ionicons/icons';
import UserSelect from "@/components/UserSelect";
import format from 'date-fns/format'
import EditShipment from "@/views/modules/lists/pages/EditShipment";

export default {
    inject: ['$http', '$toast', '$validationAlert'],
    name: 'CreateListModal',
    components: {
        IonContent, IonPage, IonLabel, IonItem, IonHeader, IonToolbar,
        IonTitle, IonButtons, IonButton, IonIcon, IonList, IonFooter,
        IonInput, IonSelect, IonSelectOption, UserSelect, IonDatetime,
        IonRow, IonCol, IonCheckbox
    },
    props: {
        value: Object
    },
    computed: {
        isEdit() {
            return this.shipment && this.shipment.id > 0;
        },
        payload() {
            const date = new Date(this.shipment.must_ship_by);
            date.setHours(23);
            date.setMinutes(59);
            date.setSeconds(59);
            const payload = {
                ...this.shipment,
                is_list: true,
                must_ship_by: this.shipment.must_ship_by?format(date, 'yyyy-MM-dd'):null,
                priority: {
                    id: this.shipment.priority.id
                }
            };
            if (this.pick) {
                payload.stage = 'pick';
            }
            return payload;
        }
    },
    mounted() {
        if (this.shipment.id > 0) {
            this.refresh();
        }
    },
    methods: {
        refresh() {
            this.$http.get(`/api/shipments/${this.shipment.id}`, {
                params: {include:'lines,lines.location,lines.item'}
            }).then(response => {
                console.log(response.data);
                this.shipment = response.data;
            });
        },
        /**
         * Because we can't get the "EditShipment" modal to listen to
         * OnModalDidPresent we're going to have to load in the full
         * version of the shipment and pass that as a prop. The modal
         * itself will have to manage the fact that lines can be
         * updated ...
         *
         * @returns {Promise<void>}
         */
        async manageItems() {
            modalController.create({
                component: EditShipment,
                componentProps: {
                    shipmentId: this.shipment.id
                }
            }).then(m => {
                m.present();
                m.onDidDismiss().then(data => {
                    if (data && data.data && data.data.refresh) {
                        this.refresh();
                    }
                });
            });
        },
        submit() {

            if (!(this.shipment?.priority?.id || this.shipment.priority.id.length < 1)) {
                this.$toast.danger("You must set a priority");
                return;
            }

            let xhr = null;
            this.disabled = true;
            if (this.isEdit) {
                xhr = this.$http.put(`/api/shipments/${this.shipment.id}`, this.payload, {params: {include:'lines'}});
            } else {
                xhr = this.$http.post('/api/shipments', this.payload, {params: {include:'lines'}});
            }
            xhr.then(response => {
                this.disabled = false;
                modalController.dismiss({
                    refresh: true
                });
                this.$toast.success(this.isEdit?"List updated":"List created");
            }).catch(error => {
                this.disabled = false;
                this.$validationAlert(error);
            });
        },
        close() {
            modalController.dismiss();
        }
    },
    data() {
        return {
            disabled: false,
            pick: false,
            shipment: {
                must_ship_by: '',
                owner: {},
                priority: {
                    id: 0
                },
                method: 'UPS',
                reference: '',
                ...this.value,
            }
        }
    },
    setup() {
        return {
            closeOutline
        };
    }
}
</script>
